import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeStyle as _normalizeStyle, toDisplayString as _toDisplayString, withCtx as _withCtx, createBlock as _createBlock, resolveDynamicComponent as _resolveDynamicComponent, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "table" }
const _hoisted_2 = { class: "table-header" }
const _hoisted_3 = {
  key: 0,
  class: "table-filters"
}
const _hoisted_4 = {
  key: 1,
  class: "table-extend"
}

import { Table, Button, Popconfirm, Tooltip } from 'ant-design-vue';
import { Tag, Filters } from '@/components';
import { Filter } from '@/components/icons';
import type { ColumnType } from 'ant-design-vue/lib/table/interface';
import Empty from './empty.vue';
import Dropdown from './dropdown.vue';
import NoSearch from './no-search.vue';
import { reactive, toRaw, useSlots, watchEffect } from 'vue';
import type { TableProps } from 'ant-design-vue';
import type { FiltersType } from '@/components/filters/index.vue';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { engageLabCore } from '@jg/qiankun-micro-tool';
export type Pagination = {
  showSizeChanger?: boolean;
  current?: number;
  pageSize?: number;
  total?: number;
  hideOnSinglePage?: boolean;
};
export type TableChange = (state: TableState) => void;
export type TableState = {
  pagination: Pagination;
  filters?: FiltersType;
  sorter?: { order: 'ascend' | 'descend'; field: string };
  dropdownFilters?: Array<{
    filters?: Array<{ text: string; value: string | number }>;
    field: string;
  }>;
};
export type ActionsBase<T, Param> = T | ((record: Param) => T);
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export interface TableColumnsType<T = Record<string, any>> extends ColumnType {
  title: string;
  ellipsis?: boolean;
  fixed?: 'left' | 'right';
  dataIndex?: keyof T;
  type?: 'ACTION' | 'TAG' | 'DATE';
  customRender?: (record: { record: T; text: string }) => JSX.Element;
  customRendeNode?: (record: T) => JSX.Element;
  actions?: Array<{
    name: ActionsBase<string, T>;
    disabled?: ActionsBase<boolean, T>;
    event?: boolean;
    action?: T | ((record: T) => void);
    hidden?: ActionsBase<boolean, T>;
    confirm?: {
      title: string;
      okText: string;
      cancelText: string;
    };
  }>;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    columns: {},
    data: {},
    scrollX: {},
    scrollY: {},
    loading: { type: Boolean },
    pageSize: { default: 10 },
    filters: {},
    current: {},
    readonly: { type: Boolean }
  },
  emits: ["change"],
  setup(__props: any, { emit: __emit }) {

/*
 * @author: Lew
 * @e-mail: yaol@jiguang.cn
 * @date: 2022/09/08 10:49:12
 * @description: 表格
 */
dayjs.extend(utc);
dayjs.extend(timezone);
const props = __props;
const slot = useSlots();
const emit = __emit;

const tableState = reactive<TableState>({
  pagination: {
    showSizeChanger: true,
    current: 1,
    pageSize: props.pageSize,
    total: props.data?.total,
    hideOnSinglePage: true,
  },
  filters: props.filters,
});
const tbz = (timestamp: number) => {
  if (typeof timestamp !== 'number') return timestamp;
  const locale = engageLabCore.locale.takeImmediately();
  switch (locale) {
    case 'zh_TW':
      return dayjs(timestamp).format('YYYY/MM/DD  HH:mm');
    case 'zh_CN':
      return dayjs(timestamp).format('YYYY/MM/DD  HH:mm');
    case 'en_US':
      return dayjs(timestamp).format('MM/DD/YYYY  HH:mm');
    default:
      return dayjs(timestamp).format('YYYY/MM/DD  HH:mm');
  }
};
// console.log(dayjs(1664261220000).tz('Etc/GMT-8', true).format('YYYY/MM/DD · HH:mm'));
const formatColumns = (columns: Array<TableColumnsType>): Array<ColumnType> =>
  columns
    .map((item, index) => {
      if (!index) Object.assign(item, { fixed: 'left' });
      else if (index === columns.length - 1) Object.assign(item, { fixed: 'right' });
      if (item.type === 'ACTION' && props.readonly) return undefined as any;
      if (item.filters) Object.assign(item, { customFilterDropdown: true });
      return item;
    })
    .filter(Boolean);
const handleTableChange: TableProps['onChange'] = (pagination, _, sorter: any) => {
  tableState.pagination = pagination;
  tableState.sorter = { order: sorter?.order, field: sorter?.field };
  emit('change', toRaw(tableState));
};
const handleFiltersChange = () => {
  emit('change', toRaw(tableState));
};
const handleDropdownChange = (
  field: string,
  filters?: Array<{ text: string; value: string | number }>,
) => {
  if (!tableState?.dropdownFilters) tableState.dropdownFilters = [];
  const index = tableState.dropdownFilters.findIndex(({ field: _field }) => _field === field) ?? -1;
  if (index < 0) tableState.dropdownFilters.push({ field, filters });
  else {
    if (filters) tableState.dropdownFilters[index].filters = filters;
    else tableState.dropdownFilters.splice(index, 1);
  }
  emit('change', toRaw(tableState));
};
watchEffect(() => {
  tableState.pagination.total = props.data?.total;
  tableState.pagination.current = props.current;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, [
      (tableState.filters)
        ? (_openBlock(), _createElementBlock("section", _hoisted_3, [
            _createVNode(_unref(Filters), {
              "min-width": "360px",
              options: tableState.filters,
              disabled: !(tableState.filters?.filter(Boolean).length || __props.data?.list.length),
              onChange: handleFiltersChange
            }, null, 8, ["options", "disabled"])
          ]))
        : _createCommentVNode("", true),
      (_unref(slot).extend?.())
        ? (_openBlock(), _createElementBlock("section", _hoisted_4, [
            _renderSlot(_ctx.$slots, "extend")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createVNode(_unref(Table), {
      pagination: tableState.pagination,
      columns: formatColumns(__props.columns),
      "data-source": __props.data?.list,
      scroll: { x: __props.scrollX, y: __props.scrollY },
      loading: __props.loading,
      onChange: handleTableChange
    }, {
      customFilterDropdown: _withCtx(({ column }) => [
        _createVNode(Dropdown, {
          options: column.filters,
          loading: __props.loading,
          onChange: selected => handleDropdownChange(column.dataIndex, selected)
        }, null, 8, ["options", "loading", "onChange"])
      ]),
      customFilterIcon: _withCtx(({ column }) => [
        _createElementVNode("span", {
          style: _normalizeStyle({
            color: tableState.dropdownFilters?.find(({ field }) => field === column.dataIndex)
              ? '#108ee9'
              : undefined,
          })
        }, [
          _createVNode(_unref(Filter), { style: {"font-size":"16px"} })
        ], 4)
      ]),
      bodyCell: _withCtx(({ column, record, text }) => [
        (column.hasTip)
          ? (_openBlock(), _createBlock(_unref(Tooltip), {
              key: 0,
              title: text
            }, {
              default: _withCtx(() => [
                _createElementVNode("span", null, _toDisplayString(text), 1)
              ]),
              _: 2
            }, 1032, ["title"]))
          : _createCommentVNode("", true),
        (column.customRendeNode)
          ? (_openBlock(), _createBlock(_resolveDynamicComponent(column.customRendeNode(record)), { key: 1 }))
          : (column?.type === 'TAG')
            ? (_openBlock(), _createBlock(_unref(Tag), {
                key: 2,
                type: record['tagType']
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(record?.[column.dataIndex]), 1)
                ]),
                _: 2
              }, 1032, ["type"]))
            : (column?.type === 'ACTION')
              ? (_openBlock(true), _createElementBlock(_Fragment, { key: 3 }, _renderList(column?.actions.filter(
              (action) =>
                !action?.hidden ||
                (action?.hidden && typeof action.hidden === 'function'
                  ? !action.hidden(toRaw(record))
                  : true),
            ), (action, index) => {
                  return (_openBlock(), _createBlock(_unref(Button), {
                    key: index,
                    type: "link",
                    disabled: 
              action?.disabled && typeof action.disabled === 'function'
                ? action.disabled(toRaw(record))
                : action.disabled
            ,
                    onClick: 
              async () => {
                if (action?.confirm) return;
                await action.action(toRaw(record));
                if (action?.event) emit('change', toRaw(tableState));
              }
            
                  }, {
                    default: _withCtx(() => [
                      (action?.confirm)
                        ? (_openBlock(), _createBlock(_unref(Popconfirm), {
                            key: 0,
                            title: action?.confirm.title,
                            "ok-text": action?.confirm.okText,
                            "cancel-text": action?.confirm.cancelText,
                            onConfirm: 
                async () => {
                  await action.action(toRaw(record));
                  if (action?.event) emit('change', toRaw(tableState));
                }
              
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(action?.name && typeof action.name === 'function'
                  ? action.name(toRaw(record))
                  : action.name), 1)
                            ]),
                            _: 2
                          }, 1032, ["title", "ok-text", "cancel-text", "onConfirm"]))
                        : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                            _createTextVNode(_toDisplayString(action?.name && typeof action.name === 'function'
                  ? action.name(toRaw(record))
                  : action.name), 1)
                          ], 64))
                    ]),
                    _: 2
                  }, 1032, ["disabled", "onClick"]))
                }), 128))
              : (column?.type === 'DATE')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                    _createTextVNode(_toDisplayString(tbz(record?.[column.dataIndex])), 1)
                  ], 64))
                : _createCommentVNode("", true)
      ]),
      emptyText: _withCtx(() => [
        (tableState.filters?.filter(Boolean).length)
          ? (_openBlock(), _createBlock(NoSearch, { key: 0 }))
          : (_openBlock(), _createBlock(Empty, { key: 1 }))
      ]),
      _: 1
    }, 8, ["pagination", "columns", "data-source", "scroll", "loading"])
  ]))
}
}

})